/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import Typography from '@material-ui/core/Typography';
import {
    makeStyles,
} from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import {
    string, shape, bool, object,
} from 'prop-types';
import MuiDialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';

const hasBorder = (promoStyles, theme) => {
    const showBorder = promoStyles.border || true;
    if (showBorder) {
        return { border: `solid 1px ${promoStyles.borderColor || theme.palette?.cms?.checkbox || '#666'}` };
    }
    return {};
};

const useStyles = makeStyles((theme) => ({
    promoToggleMessage: ({ cmsPositioning }) => ({
        height: 'auto',
        margin: cmsPositioning === 'Bottom' ? '0 0 1rem' : '1rem 0 0',
        borderRadius: '3px',
        color: theme.palette.submit,
        fontFamily: theme.palette.Typography,
        cursor: 'pointer',
        '@media screen and (max-width: 959px)': {
            marginTop: '1rem',
            '& [class*="text"]': {
                display: 'flex',
                flexDirection: 'column',
            },
            '& [data-testid="see-promo-details"]': {
                marginTop: '.2rem',
                alignSelf: 'self-end',
            },
        },
    }),
    promoMessage: ({ promoStyles, featureFlags }) => ({
        height: 'auto',
        borderRadius: theme?.palette?.pdp?.promoSection?.borderRadius || '3px',
        backgroundColor: promoStyles.background !== promoStyles.color ? promoStyles.background : '#fff',
        color: promoStyles.color !== promoStyles.background ? promoStyles.color : '#1f1f1b',
        marginBottom: theme?.palette?.pdp?.promoSection?.marginDesktop || '1rem',
        padding: theme?.palette?.pdp?.promoSection?.textPadding || '10px',
        fontFamily: featureFlags?.['is-pdp-new-layout-enabled'] ? theme?.typography?.secondaryFontFamily : theme.typography?.fontFamily,
        fontSize: theme?.palette?.pdp?.promoSection?.fontSize || '',
        fontWeight: theme?.palette?.pdp?.promoSection?.fontWeight || '',
        lineHeight: theme?.palette?.pdp?.promoSection?.lineHeight || '',
        letterSpacing: theme?.palette?.pdp?.promoSection?.letterSpacing || '',
        textAlign: theme?.palette?.pdp?.promoSection?.textAlign || '',
        [theme.breakpoints.down('sm')]: {
            fontSize: theme?.palette?.pdp?.promoSection?.mobileFontSize || '',
            lineHeight: theme?.palette?.pdp?.promoSection?.mobileLineHeight || '',
            padding: theme?.palette?.pdp?.promoSection?.paddingTextMobile || '10px',
            marginBottom: theme?.palette?.pdp?.promoSection?.marginMobile || '1rem',
        },
    }),
    promoMessageBorder: ({ promoStyles }) => (hasBorder(promoStyles, theme)),
    promoMessageImage: {
        width: '100%',
        height: 'auto',
        borderRadius: '3px',
        border: `1px solid ${theme.palette.cms?.checkbox}`,
        margin: '0 auto 30px',
        clear: 'both', // TODO: Remove this when all floats are removed from styles.
    },
    parseMarkPromo: {
        display: 'inline-block',
        marginLeft: '20px',
        cursor: 'pointer',
        textDecoration: 'underline',
        outline: 'none',
    },
    dialogCopy: {
        width: '91%',
    },
    dialogContent: {
        width: '100%',
        display: 'flex',
    },
    text: {
        [theme.breakpoints.down(450)]: {
            width: '280px',
        },
        [theme.breakpoints.up(960)]: {
            width: '450px',
        },
        [theme.breakpoints.up(1150)]: {
            width: '600px',
        },
    },
    dots: ({ promoOpen }) => ({
        display: promoOpen ? 'none' : 'inline',
    }),
    moreText: {
        display: 'inline',
        width: 'auto',
        marginRight: '50px',
    },
    button: {
        marginLeft: '15px',
        width: '25px',
        height: '25px',
        textAlign: 'center',
        verticalAlign: 'middle',
        backgroundColor: '#FFFFFF',
        color: theme.palette.cms?.productDetailsSummary || '#000',
        fontSize: 'medium',
    },
    promoMessageContainer: ({ promoStyles }) => ({
        display: 'flex',
        justifyContent: 'center',
        boxShadow: 'none',
        border: `1px solid ${promoStyles.borderColor || '#ccc'}`,
        padding: '10px',
        backgroundColor: promoStyles.background !== promoStyles.color ? promoStyles.background : '#fff',
        color: promoStyles.color !== promoStyles.background ? promoStyles.color : '#1f1f1b',
    }),
    panelRoot: {
        boxShadow: 'none',
        backgroundColor: 'inherit',
    },
    panelContent: {
        margin: '12px 0 !important',
    },
    clubPromoCode: {
        display: 'block',
        '& strong': {
            '&:first-child': {
                display: 'block',
            },
        },
    },
    clubPromoCodeContainer: {
        display: 'flex',
        border: '1px solid rgba(47, 47, 47, 0.3)',
        padding: '17px',
        width: '75%',
        '@media only screen and (max-width: 1024.5px) ': {
            width: '95%',
            margin: '0 auto',
            padding: '12px',
        },
        '@media only screen and (max-width: 600px) ': {
            width: '100%',
        },
        '& img': {
            paddingRight: '15px',
        },
        '& $parseMarkPromo': {
            '@media only screen and (max-width: 768px) ': {
                marginLeft: '10px',
            },
            '@media only screen and (max-width: 600px) ': {
                marginLeft: '0px',
                display: 'block',
            },

        },
    },
    promoDescMessage: ({ promoStyles }) => ({
        color: promoStyles.color,
        textAlign: theme?.palette?.pdp?.promoSection?.textAlign || 'center',
        fontSize: theme.typography.fontSize,
        lineHeight: theme?.palette?.pdp?.promoSection?.descLineHeight || '24px',
        letterSpacing: theme?.palette?.pdp?.promoSection?.descLetterSpacing || '0.02em',
        paddingTop: theme?.palette?.pdp?.promoSection?.descPaddingTop || '8px',
        [theme.breakpoints.down('sm')]: {
            fontSize: theme?.palette?.pdp?.promoSection?.descMobileFontSize || '14px',
            lineHeight: theme?.palette?.pdp?.promoSection?.descMobileLineHeight || '20px',
        },
    }),
    promoWrapper: {
        padding: '16px 24px !important',
        border: '0px  !important',
        background: '#fafafa  !important',
        [theme.breakpoints.down('600')]: {
            width: 'calc(100% - 32px)',
            margin: '0 auto',
            padding: '16px !important',
            fontSize: '14px',
            lineHeight: '22px',
            letterSpacing: '0.5px',
        },
    },
}));

const GraphqlPDPPromotionalBlock = ({
    promoSummary, position, promoStyles, clubProductPage, featureFlags, isPdpRedesignEnabled,
}) => {
    const [promoOpen, setPromoOpen] = useState(false);
    const [open, setOpen] = useState();
    const promoCopy = promoSummary?.pdp_message || '';
    const promoCode = promoSummary?.promotion_code || '';
    const promoDesc = promoSummary?.promotion_details || '';
    const imagePres = promoSummary?.image_presentation;
    const toggleMessage = promoSummary?.collapse_message || false;
    const cmsPositioning = promoSummary?.pdp_message_positioning || 'Bottom';
    const renderMessage = cmsPositioning === position;
    const text = `${promoCopy.slice(0, 81)}...`; // limit the copy to one line
    // If there is any stray unterminated markdown bolding before the ellipis, remove it
    // This regex replaces any combination of a single or double asterisk and any copy w/o termination by another double asterisk before the ellipsis
    // \s (a space) \*+ (one or more asterisks) [a-zA-Z]* (zero or more upper or lowercase letters immediately following the asterisks) \.\.\. (...followed by the ellipsis)
    const truncatedText = text.replace(/(\s\*+[a-zA-Z]*\.\.\.)/g, '...');
    const classes = useStyles({
        promoOpen, cmsPositioning, promoStyles, featureFlags,
    });

    const handleModal = (e) => {
        e.preventDefault();
        e.persist();
        e.stopPropagation();
        setOpen(true);
    };
    const onClose = () => {
        setOpen(false);
    };

    const toggleMessageVisibility = () => setPromoOpen(!promoOpen);

    const dialog = (
        <Dialog open={open}>
            <div className={classes.dialogContent}>
                <div className={classes.dialogCopy}>
                    <DialogTitle id="customized-dialog-title">
                        Details for: <span>{promoCode}</span>
                    </DialogTitle>
                </div>
                <div>
                    <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </div>
            </div>
            <MuiDialogContent dividers>
                <Typography gutterBottom>
                    <ReactMarkdown source={promoDesc} escapeHtml={false} />
                </Typography>
            </MuiDialogContent>
        </Dialog>
    );

    if (!promoSummary) {
        return null;
    }

    if (imagePres && renderMessage) {
        return (
            <>
                <div
                    data-testid="see-promo-details"
                    tabIndex={0}
                    role="link"
                    onKeyDown={() => { }}
                    onClick={(e) => { handleModal(e, true); }}
                >
                    <img className={classes.promoMessageImage} src={imagePres.url} alt={imagePres.title} />
                </div>
                {open ? dialog : null}
            </>
        );
    }
    if (renderMessage && !toggleMessage) {
        return (
            <div className={clubProductPage && classes.clubPromoCodeContainer}>
                {clubProductPage && <img src="https://images.contentstack.io/v3/assets/blt89dbf1c763ec00a6/blt52f52d9442f151dc/63d934525ba46f75ddba10c8/Union.svg?auto=webp" alt="union icon image" />}
                <div
                    className={clubProductPage ? classes.clubPromoCode : `${classes.promoMessage} ${classes.promoMessageBorder} ${isPdpRedesignEnabled ? classes.promoWrapper : ''}`}
                >
                    <ReactMarkdown
                        escapeHtml={false}
                        renderers={{ root: React.Fragment }}
                        source={promoCopy}
                        disallowedTypes={['paragraph']}
                        unwrapDisallowed
                    />
                    {promoDesc && !featureFlags?.['is-pdp-new-layout-enabled'] && (
                        <div
                            data-testid="see-promo-details"
                            tabIndex={0}
                            role="link"
                            onKeyDown={() => { }}
                            className={classes.parseMarkPromo}
                            onClick={(e) => { handleModal(e, true); }}
                        >
                            See Details
                        </div>
                    ) }
                    {promoDesc && featureFlags?.['is-pdp-new-layout-enabled'] && (<div className={classes.promoDescMessage}> {promoDesc}</div>)}
                </div>
                {open ? dialog : null}
            </div>
        );
    }
    if (renderMessage && toggleMessage) {
        return (
            <>
                <div
                    className={classes.promoToggleMessage}
                >
                    { /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */ }
                    <div className={classes.promoMessageContainer} role="banner" onKeyDown={() => { }} onClick={toggleMessageVisibility}>
                        <div className={classes.text}>
                            {!promoOpen && (
                                <ReactMarkdown
                                    escapeHtml={false}
                                    renderers={{ root: React.Fragment }}
                                    source={truncatedText}
                                    disallowedTypes={['paragraph']}
                                    unwrapDisallowed
                                />
                            )}
                            {promoOpen && (
                                <>
                                    <div className={classes.moreText} data-testid="see-promo-message">
                                        <ReactMarkdown
                                            escapeHtml={false}
                                            renderers={{ root: React.Fragment }}
                                            source={promoCopy}
                                            disallowedTypes={['paragraph']}
                                            unwrapDisallowed
                                        />
                                    </div>
                                    {promoDesc.length ? (
                                        <div
                                            data-testid="see-promo-details"
                                            tabIndex={0}
                                            role="link"
                                            onKeyDown={() => { }}
                                            className={classes.parseMarkPromo}
                                            onClick={(e) => { handleModal(e, true); }}
                                        >
                                            See Details
                                        </div>
                                    ) : null}
                                </>
                            )}
                        </div>
                        <div toggle>
                            <IconButton
                                data-testid="see-promo-message-button"
                                aria-label={promoOpen ? 'close' : 'open'}
                                className={classes.button}
                            >
                                {promoOpen ? <RemoveCircleOutlineIcon /> : <AddCircleOutlineIcon />}
                            </IconButton>
                        </div>
                    </div>

                </div>
                {dialog}
            </>
        );
    }
    return <></>;
};

GraphqlPDPPromotionalBlock.defaultProps = {
    position: 'Bottom',
    promoStyles: {},
    clubProductPage: false,
    isPdpRedesignEnabled: false,
};

GraphqlPDPPromotionalBlock.propTypes = {
    promoSummary: shape({
        pdp_message: string,
        promotion_code: string,
        promotion_details: string,
    }).isRequired,
    position: string,
    promoStyles: shape({
        background: string,
        color: string,
        border: string,
        borderColor: string,
    }),
    clubProductPage: bool,
    featureFlags: object.isRequired,
    isPdpRedesignEnabled: bool,
};

export default GraphqlPDPPromotionalBlock;
